(() => {
  if (customElements.get('sales-popularity')) return;

  class SalesPopularity extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.valueNode = this.querySelector('[data-sp-value]');
      this.goalNode = this.querySelector('[data-sp-goal]');
      this.percentNode = this.querySelector('[data-sp-percent]');
      this.messageNode = this.querySelector('[data-sp-message]');
      this.progressNode = this.querySelector('[data-sp-progress]');
      this.statusNode = this.querySelector('[data-sp-status]');
      this.currentValue = this.number('initialValue', 0);
      this.mount();
      this.watchVariant();
      this.refresh(true);

      const interval = Math.max(0, this.number('refreshInterval', 0));
      if (interval > 0) this.timer = window.setInterval(() => this.refresh(false), interval);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      document.removeEventListener('change', this.onVariantChange);
    }

    number(name, fallback) {
      const value = Number(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    mount() {
      const selector = this.dataset.mountSelector?.trim();
      if (!selector) return;

      let target;
      try {
        target = document.querySelector(selector);
      } catch (error) {
        console.warn('Sales popularity: invalid mount selector.', error);
        return;
      }
      if (!target || target === this || this.contains(target)) return;

      const position = this.dataset.mountPosition || 'afterend';
      if (position === 'inside-start') target.prepend(this);
      else if (position === 'inside-end') target.append(this);
      else target.insertAdjacentElement(position, this);
    }

    watchVariant() {
      const selector = this.dataset.variantSelector?.trim();
      if (!selector) return;

      this.onVariantChange = (event) => {
        let input;
        try {
          input = event.target?.matches?.(selector) ? event.target : null;
        } catch (error) {
          console.warn('Sales popularity: invalid variant selector.', error);
          document.removeEventListener('change', this.onVariantChange);
          return;
        }
        if (!input?.value) return;
        this.dataset.variantId = input.value;
        this.refresh(true);
      };
      document.addEventListener('change', this.onVariantChange);
    }

    async refresh(immediate) {
      let value = this.number('initialValue', 0);
      const source = this.dataset.sourceMode || 'configured';

      if (source === 'api' || source === 'api_with_fallback') {
        const remoteValue = await this.fetchValue();
        if (remoteValue !== null) value = remoteValue;
        else if (source === 'api') return this.setUnavailable();
      }

      this.render(Math.max(0, value), immediate);
    }

    async fetchValue() {
      const endpoint = this.buildEndpoint();
      if (!endpoint) return null;

      try {
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
          credentials: this.dataset.sendCredentials === 'true' ? 'same-origin' : 'omit'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const value = this.readPath(payload, this.dataset.responsePath || 'value');
        return Number.isFinite(Number(value)) ? Number(value) : null;
      } catch (error) {
        console.warn('Sales popularity: data request failed.', error);
        return null;
      }
    }

    buildEndpoint() {
      return (this.dataset.endpoint || '')
        .replaceAll('[product_key]', encodeURIComponent(this.dataset.productKey || ''))
        .replaceAll('[variant_id]', encodeURIComponent(this.dataset.variantId || ''));
    }

    readPath(payload, path) {
      return path.split('.').filter(Boolean).reduce((value, key) => value?.[key], payload);
    }

    format(value) {
      const maximumFractionDigits = Math.max(0, this.number('decimalPlaces', 0));
      try {
        return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || undefined, {
          notation: this.dataset.compactNumbers === 'true' ? 'compact' : 'standard',
          maximumFractionDigits
        }).format(value);
      } catch (error) {
        return String(Math.round(value));
      }
    }

    render(value, immediate) {
      this.currentValue = value;
      this.hidden = false;
      this.removeAttribute('aria-hidden');

      const goal = Math.max(1, this.number('goal', 100));
      const percent = Math.min(100, Math.max(0, (value / goal) * 100));
      const formattedValue = this.format(value);
      const formattedGoal = this.format(goal);
      const formattedPercent = this.format(percent);
      const template = this.dataset.messageTemplate || '[value]';
      const message = this.replaceTokens(template, formattedValue, formattedGoal, formattedPercent);
      const announcement = this.replaceTokens(
        this.dataset.announcementTemplate || template,
        formattedValue,
        formattedGoal,
        formattedPercent
      );

      if (this.valueNode) this.valueNode.textContent = formattedValue;
      if (this.goalNode) this.goalNode.textContent = formattedGoal;
      if (this.percentNode) this.percentNode.textContent = formattedPercent;
      if (this.messageNode) this.messageNode.setAttribute('aria-label', message);
      if (this.progressNode) {
        this.progressNode.style.setProperty('--sp-progress', `${percent}%`);
        this.progressNode.setAttribute('aria-valuenow', String(Math.round(percent)));
      }
      if (this.statusNode) this.statusNode.textContent = announcement;

      if (!immediate && this.dataset.animate !== 'false') {
        this.classList.remove('is-updating');
        void this.offsetWidth;
        this.classList.add('is-updating');
      }
    }

    replaceTokens(template, value, goal, percent) {
      return template
        .replaceAll('[value]', value)
        .replaceAll('[goal]', goal)
        .replaceAll('[percent]', percent);
    }

    setUnavailable() {
      if (this.dataset.hideWhenUnavailable === 'true') {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
      } else if (this.statusNode) {
        this.statusNode.textContent = this.dataset.unavailableText || '';
      }
    }
  }

  customElements.define('sales-popularity', SalesPopularity);
})();
